/**
 * The router's error boundary, wired as `errorComponent` on __root.
 *
 * NOT the app's only boundary — there is a React one wrapping the whole tree in
 * main.tsx. That is the point: a React error boundary never sees a ROUTER error.
 * TanStack catches those itself and renders the route's `errorComponent`, and
 * before this no route defined one — not __root, not any child. So every route
 * failure sailed past the React boundary into TanStack's unstyled default,
 * "Something went wrong!" with a Show Error toggle: no styling, no way back,
 * nothing logged. Diagnosing an incident meant a member screenshotting a blank
 * page (2026-08-17 assets, 2026-08-18 community feed — five candidate causes
 * proposed against those two screenshots, all five eliminated, because nothing
 * recorded what actually threw).
 *
 * So this does exactly two things, and deliberately no more:
 *
 *   1. Render a page a member can act on — what happened, Reload, Try again,
 *      and a short reference id, printed on the page and in the console line so
 *      a screenshot of either can be matched to the other.
 *
 *   2. Log to the CONSOLE — build version, route, online state, stack. Not to
 *      the database: see the note in report() for why `activities` is the wrong
 *      table. A durable sink needs its own, and does not exist yet.
 *
 * IT DOES NOT RELOAD. That is not an oversight. Recovery from a failed lazy
 * import already lives in ONE place — the `vite:preloadError` listener in
 * main.tsx — and that one is strictly better: it refetches the failing URL with
 * `cache: 'reload'` before reloading, which is the only thing that overwrites a
 * poisoned immutable browser-cache entry. A second reload path here would race
 * it, carry its own flag, and do the weaker repair. One recovery path.
 */
import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { isChunkLoadError } from '@/lib/chunk-errors';

const RUNNING_VERSION = import.meta.env.VITE_BUILD_VERSION || 'v1';

function shortId(): string {
  try {
    return crypto.randomUUID().slice(0, 8);
  } catch {
    return Math.abs(Date.now() ^ (performance.now() * 1000)).toString(36).slice(0, 8);
  }
}

function report(error: unknown, ref: string): void {
  const detail = {
    ref,
    name: error instanceof Error ? error.name : 'Unknown',
    message: error instanceof Error ? error.message : String(error),
    stack: error instanceof Error ? error.stack?.slice(0, 2000) : undefined,
    build: RUNNING_VERSION,
    href: typeof window !== 'undefined' ? window.location.href : null,
    online: typeof navigator !== 'undefined' ? navigator.onLine : null,
    chunk: isChunkLoadError(error),
  };

  // Console only, deliberately.
  //
  // This used to also write an `activities` row. It must not: admin_user_list
  // computes BOTH `activity_count` and `last_active` as unfiltered aggregates
  // over that table —
  //     (select count(*)      from activities a where a.user_id = p.id)
  //     (select max(created_at) from activities a where a.user_id = p.id)
  // — so an error row inflates a member's activity count and stamps them
  // "last active" at the moment their app crashed. An error would read as
  // engagement in the admin Members tab, which is worse than no telemetry.
  //
  // A durable sink is still wanted (see the reference id shown on the page —
  // it is currently only useful for matching a screenshot to a console line).
  // It needs its own table, NOT this one; `activities` is a member-engagement
  // log that three read paths aggregate without filtering by type.
  console.error('[RootError]', detail);
}

interface Props {
  error: unknown;
  /** TanStack Router: retry the failed route without a full reload. */
  reset?: () => void;
}

export function RootErrorBoundary({ error, reset }: Props) {
  const refId = useRef(shortId()).current;
  const [showDetail, setShowDetail] = useState(false);

  const reported = useRef(false);
  useEffect(() => {
    if (reported.current) return; // StrictMode double-invokes effects in dev
    reported.current = true;
    report(error, refId);
  }, [error, refId]);

  const message = error instanceof Error ? error.message : String(error);

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-6 py-10">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-center">
        <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-muted">
          <AlertTriangle className="h-5 w-5 text-muted-foreground" />
        </div>

        <h1 className="mb-2 text-lg font-semibold text-foreground">
          This page didn&apos;t load
        </h1>

        <p className="mb-5 text-sm text-muted-foreground">
          {isChunkLoadError(error)
            ? 'Reloading usually fixes this — it normally means the app updated while this page was open.'
            : 'Something went wrong loading this page. Reloading usually fixes it.'}
        </p>

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl bg-foreground px-4 text-sm font-medium text-background transition-opacity hover:opacity-90"
          >
            <RefreshCw className="h-4 w-4" />
            Reload
          </button>
          {reset && (
            <button
              type="button"
              onClick={reset}
              className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              Try again
            </button>
          )}
        </div>

        <p className="mt-5 text-xs text-muted-foreground">
          Reference <span className="font-mono">{refId}</span>
        </p>

        <button
          type="button"
          onClick={() => setShowDetail((v) => !v)}
          className="mt-2 text-xs text-muted-foreground underline underline-offset-2"
        >
          {showDetail ? 'Hide details' : 'Show details'}
        </button>

        {showDetail && (
          <pre className="mt-3 max-h-52 overflow-auto rounded-lg bg-muted p-3 text-left text-[11px] leading-relaxed text-muted-foreground">
            {message}
            {'\n\n'}build {RUNNING_VERSION}
          </pre>
        )}
      </div>
    </div>
  );
}

export default RootErrorBoundary;
